import { create } from 'zustand';

import type { ISchedule } from '@/entities/booking/model/types';

export interface BookingStore {
  doctorId: number;
  medcenterId: number;
  iinRequired: boolean;
  changedSlot?: {
    day: ISchedule;
    slot: string;
  } | null;
}

export type UseBooking = {
  medcenterId: number | null;
  doctorId: number | null;
  hasOnlineBooking: boolean;

  booking: BookingStore[];

  setMedcenterId: (id: number) => void;
  setDoctorId: (id: number) => void;
  setIinRequired: (params: { iin: boolean; id: number }) => void;
  createBooking: (params: {
    doctorId: number;
    medcenterId: number;
    initialDay: ISchedule | null;
    initialSlot: string;
  }) => void;
  setChangedSlot: (params: { day: ISchedule; slot: string; id: number }) => void;
  resetChangedSlot: (id: number) => void;
  setHasOnlineBooking: (value: boolean) => void;

  resetData: () => void;
};

export const useBooking = create<UseBooking>()(set => ({
  medcenterId: null,
  doctorId: null,
  hasOnlineBooking: false,

  booking: [],

  setMedcenterId: (id: number) => {
    set({ medcenterId: id });
  },

  setDoctorId: (id: number) => {
    set({ doctorId: id });
  },

  setIinRequired: ({ iin, id }) => {
    set(state => ({
      booking: state.booking.map(item => {
        if (item.doctorId === id) {
          return {
            ...item,
            iinRequired: iin,
          };
        } else {
          return item;
        }
      }),
    }));
  },

  createBooking({ doctorId, medcenterId, initialDay, initialSlot }) {
    set(state => ({
      booking: state.booking.find(item => item.doctorId === doctorId && item.medcenterId === medcenterId)
        ? state.booking
        : [
            ...state.booking,
            {
              doctorId,
              medcenterId,
              iinRequired: false,
              changedSlot: initialDay
                ? {
                    day: initialDay,
                    slot: initialSlot,
                  }
                : null,
            },
          ],
    }));
  },

  setChangedSlot: ({ day, slot, id }) => {
    set(state => ({
      booking: state.booking.map(item => {
        if (item.doctorId === id) {
          return {
            ...item,
            changedSlot: { day, slot },
          };
        } else {
          return item;
        }
      }),
    }));
  },

  resetChangedSlot: (id: number) => {
    set(state => ({
      booking: state.booking.map(item => {
        if (item.medcenterId === id) {
          return {
            ...item,
            changedSlot: null,
          };
        } else {
          return item;
        }
      }),
    }));
  },

  setHasOnlineBooking: (value: boolean) => {
    set({ hasOnlineBooking: value });
  },

  resetData: () => {
    set({
      booking: [],
      doctorId: null,
      medcenterId: null,
      hasOnlineBooking: false,
    });
  },
}));
